import React from 'react';
import {
  InlineNotification,
  Table,
  TableColumns,
  TableSortBy,
  useTheme
} from '@veneer/core';

import { useDependencyManagerContext } from 'src/contexts/dependencyManager';
import ApiError from 'src/types/error';

import { TableContainer, TableHeaderRow, ErrorContainer } from '../styled';

const LOADING_ROWS_COUNT = 3;

export type SharedTableLayoutProps = {
  testIdPrefix: string;
  title: string;
  emptyMessage: string;
  error: ApiError | null;
  isErrored: boolean;
  isLoading: boolean;
  onRetry: () => void;
  columns: TableColumns[];
  tableData: Record<string, unknown>[];
  headerAction?: React.ReactNode;
  sortBy?: TableSortBy;
  onSort?: (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    sort: TableSortBy
  ) => void;
};

const SharedTableLayout: React.FC<SharedTableLayoutProps> = ({
  testIdPrefix,
  title,
  emptyMessage,
  error,
  isErrored,
  isLoading,
  onRetry,
  columns,
  tableData,
  headerAction,
  sortBy,
  onSort
}) => {
  const { translate } = useDependencyManagerContext();
  const theme = useTheme();

  return (
    <TableContainer
      $veneerTheme={theme}
      data-testid={`react-device-management-mfe-${testIdPrefix}`}
    >
      <TableHeaderRow>
        <p
          className="subtitle-large"
          data-testid={`react-device-management-mfe-${testIdPrefix}-title`}
        >
          {title}
        </p>
        {headerAction}
      </TableHeaderRow>

      {isErrored && error && (
        <ErrorContainer>
          <InlineNotification
            closeButton={false}
            description={translate(error.message)}
            type="negative"
            data-testid={`react-device-management-mfe-${testIdPrefix}-error`}
            action={{
              action: onRetry,
              label: translate('components.device-category.button.retry-button')
            }}
            actionPlacement="top"
            aria-live="polite"
          />
        </ErrorContainer>
      )}

      {!isErrored && (
        <Table
          columns={columns}
          data={tableData}
          loading={isLoading}
          loadingDataLength={LOADING_ROWS_COUNT}
          containerOutline
          {...(onSort && { onSort })}
          customNoItems={
            <p
              className="label-regular"
              style={{ color: theme.color.foreground.disabled }}
              data-testid={`react-device-management-mfe-${testIdPrefix}-empty`}
            >
              {emptyMessage}
            </p>
          }
          preferences={{
            width: [{ columnId: 'spacer', width: 32 }],
            ...(sortBy && { sortBy })
          }}
          data-testid={`react-device-management-mfe-${testIdPrefix}-data`}
        />
      )}
    </TableContainer>
  );
};

export default SharedTableLayout;
